import { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import client from "@/lib/api";

const VID_KEY = "tw_vid";

function getVisitorId() {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) {
      // RFC4122 v4 — crypto.randomUUID when available, fallback to Math.random hex.
      v = (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : "vx-" + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
      localStorage.setItem(VID_KEY, v);
    }
    return v;
  } catch (_) {
    return null; // private mode / blocked storage
  }
}

/**
 * Mount inside the router. Sends a single beacon to /api/analytics/pageview
 * for every route change. Admin paths are skipped server-side too.
 *
 * Fire-and-forget — never blocks the UI and silently swallows errors.
 * Includes an anonymous, browser-local visitor_id so the backend can dedupe
 * unique daily visitors without storing any PII.
 */
export default function PageviewTracker() {
  const location = useLocation();
  const lastSent = useRef(null);

  useEffect(() => {
    const fullPath = location.pathname + (location.search || "");
    // Dedupe in-session repeat navigations to the same URL (e.g. React StrictMode double effect).
    if (lastSent.current === fullPath) return;
    lastSent.current = fullPath;

    // Skip admin/internal paths.
    if (location.pathname.startsWith("/admin")) return;

    const body = {
      path: location.pathname,
      referrer: typeof document !== "undefined" ? document.referrer : "",
      visitor_id: getVisitorId(),
    };

    // Use sendBeacon when available — survives page unload, no CORS preflight.
    try {
      const url = `${client.defaults.baseURL}/analytics/pageview`;
      const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
      if (typeof navigator !== "undefined" && navigator.sendBeacon && navigator.sendBeacon(url, blob)) {
        return;
      }
    } catch (_) { /* fall through to axios */ }

    client.post("/analytics/pageview", body).catch(() => {});
  }, [location.pathname, location.search]);

  return null;
}
